import { MapPin, Truck, Activity, AlertTriangle, Shield } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const BaseCard = ({ base, onSelectBase }) => {
  const { getThemeClasses, theme } = useTheme();
  const themeClasses = getThemeClasses();
  // Calculate metrics for the base
  const vehicleCount = base.vehicles.length;
  const avgHealth = base.vehicles.length > 0
    ? (base.vehicles.reduce((sum, v) => sum + v.health, 0) / base.vehicles.length).toFixed(1)
    : 0;
  const activeAlerts = base.alerts;

  // Calculate operational readiness
  const readyVehicles = base.vehicles.filter(v => v.health >= 70 && v.alertLevel === 'low');
  const operationalReadiness = base.vehicles.length > 0
    ? ((readyVehicles.length / base.vehicles.length) * 100).toFixed(1)
    : 0;

  // Count high alerts
  const highAlerts = base.vehicles.filter(v => v.alertLevel === 'high').length;

  return (
    <div
      onClick={() => onSelectBase(base.id)}
      className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-4 hover:border-military-500/50 hover:scale-105 transform transition-all duration-200 cursor-pointer shadow-md`}
    >
      {/* Header: Base Name and Location */}
      <div className={`mb-4 pb-3 border-b ${themeClasses.border}`}>
        <h3 className={`text-lg font-bold ${themeClasses.textPrimary} mb-1`}>{base.name}</h3>
        <div className={`flex items-center ${themeClasses.textMuted} text-sm`}>
          <MapPin size={14} className="mr-1" />
          <span>{base.location}</span>
        </div>
      </div>

      {/* 4 Metric Cards in 2x2 Grid */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        {/* Vehicles */}
        <div className="bg-gradient-to-br from-green-600/20 to-green-700/20 border border-green-500/30 rounded-lg p-3">
          <div className="flex items-center justify-between mb-1">
            <span className={`${themeClasses.textSecondary} text-xs font-medium`}>Vehicles</span>
            <Truck size={16} className="text-green-400" />
          </div>
          <p className={`${themeClasses.textPrimary} text-xl font-bold`}>{vehicleCount}</p>
        </div>

        {/* Avg Health */}
        <div className="bg-gradient-to-br from-military-600/20 to-military-700/20 border border-military-500/30 rounded-lg p-3">
          <div className="flex items-center justify-between mb-1">
            <span className={`${themeClasses.textSecondary} text-xs font-medium`}>Avg Health</span>
            <Activity size={16} className="text-military-400" />
          </div>
          <p className={`${themeClasses.textPrimary} text-xl font-bold`}>{avgHealth}%</p>
        </div>

        {/* Active Alerts */}
        <div className="bg-gradient-to-br from-amber-600/20 to-amber-700/20 border border-amber-500/30 rounded-lg p-3">
          <div className="flex items-center justify-between mb-1">
            <span className={`${themeClasses.textSecondary} text-xs font-medium`}>Alerts</span>
            <AlertTriangle size={16} className="text-amber-400" />
          </div>
          <p className={`${themeClasses.textPrimary} text-xl font-bold`}>{activeAlerts}</p>
        </div>

        {/* Readiness */}
        <div className="bg-gradient-to-br from-purple-600/20 to-purple-700/20 border border-purple-500/30 rounded-lg p-3">
          <div className="flex items-center justify-between mb-1">
            <span className={`${themeClasses.textSecondary} text-xs font-medium`}>Readiness</span>
            <Shield size={16} className="text-purple-400" />
          </div>
          <p className={`${themeClasses.textPrimary} text-xl font-bold`}>{operationalReadiness}%</p>
        </div>
      </div>

      {/* High Alerts Warning at Bottom */}
      {highAlerts > 0 && (
        <div className={`${theme === 'light' ? 'bg-red-100 border border-red-300' : 'bg-red-600/20 border border-red-500/40'} rounded-lg p-2 flex items-center justify-between`}>
          <div className="flex items-center">
            <AlertTriangle size={16} className="text-red-400 mr-2" />
            <span className={`${theme === 'light' ? 'text-red-800' : 'text-red-200'} text-sm font-medium`}>High Priority Alerts</span>
          </div>
          <span className={`${theme === 'light' ? 'text-red-900' : 'text-red-100'} text-sm font-bold`}>{highAlerts}</span>
        </div>
      )}

      {highAlerts === 0 && (
        <div className={`${theme === 'light' ? 'bg-green-100 border border-green-300' : 'bg-green-600/20 border border-green-500/40'} rounded-lg p-2 flex items-center justify-center`}>
          <span className={`${theme === 'light' ? 'text-green-800' : 'text-green-200'} text-sm font-medium`}>No High Priority Alerts</span>
        </div>
      )}
    </div>
  );
};

export default BaseCard;
